import moment from 'moment';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import {
  getHdLocksKeysLockMeta,
  getHdLocksKeysPeriodMeta,
  HdLocksKeysPeriodId,
  HdLocksKeysPeriodMeta,
  hdLocksKeysKeyGate,
} from './hd-locks-keys-metadata';

/**
 * Teksten van een periode en van de sleutels erin, met terugval voor de
 * periodes zonder eigen content (de meeste van de 49 tijdperken). Eigen content
 * leeft in de locks-keys-scope (`periods.<id>.*`, `locks.<gate>.keys.<id>.*`);
 * zonder die content spreken de jaartallen en kruisnamen van de Global
 * Cycles-tabel en de Rave I'Ching-keynote van de sleutelpoort zelf, zoals in de
 * klas: de keynote blijft het referentiepunt.
 */

export interface HdLocksKeysPeriodText {
  /** Kort label voor toggles en lijsten, bv. "1615 – 2026". */
  readonly label: string;
  readonly vesselCross: string;
  readonly sphinxCross: string;
  /** Beide kruisen, bv. "Cross of Planning & Cross of Maya". */
  readonly crosses: string;
  /** Eén zin over het tijdperk; leeg wanneer er geen eigen content is. */
  readonly keynote: string;
  /**
   * Wanneer het tijdperk start: het exacte moment van zijn cycle chart in GMT
   * waar de bron het geeft, anders het startjaar.
   */
  readonly start: string;
}

export interface HdLocksKeysKeyText {
  readonly name: string;
  readonly circuit: string;
  readonly cross: string;
  readonly description: string;
  readonly planetaryNuance: string;
  readonly channelNote: string;
  readonly starNote2027: string;
  /** True wanneer de tekst uit de poort-keynotes komt in plaats van eigen content. */
  readonly isGeneric: boolean;
}

type HdLocksKeysTranslate = (key: string, params?: Record<string, unknown>) => string;

function translator(gateInfo: HdGateInfoService, lang: string): HdLocksKeysTranslate {
  return (key, params) => gateInfo.locksKeysTextIn(key, lang, params);
}

/** "1615 – 2026", "16513 – 16103 v.Chr." of "33 v.Chr. – 378 n.Chr." */
export function hdLocksKeysYearRangeLabel(gateInfo: HdGateInfoService, lang: string, meta: HdLocksKeysPeriodMeta): string {
  const t = translator(gateInfo, lang);
  const bc = t('periods.eraBc');
  const ad = t('periods.eraAd');
  if (meta.yearFrom > 0 && meta.yearTo > 0) {
    return `${meta.yearFrom} – ${meta.yearTo}`;
  }
  if (meta.yearFrom < 0 && meta.yearTo < 0) {
    return `${-meta.yearFrom} – ${-meta.yearTo} ${bc}`;
  }
  return `${-meta.yearFrom} ${bc} – ${meta.yearTo} ${ad}`;
}

/** "Start: 15 februari 2027, 22:10 GMT", of zonder overgeleverd tijdstip "Start: 1615 (…)". */
export function hdLocksKeysStartLabel(gateInfo: HdGateInfoService, lang: string, meta: HdLocksKeysPeriodMeta): string {
  const t = translator(gateInfo, lang);
  if (meta.startsAtUtc != null) {
    const at = moment.utc(meta.startsAtUtc).locale(momentLocaleFor(lang)).format('D MMMM YYYY, HH:mm');
    return t('periods.startExact', { moment: at });
  }
  const year = meta.yearFrom > 0 ? `${meta.yearFrom}` : `${-meta.yearFrom} ${t('periods.eraBc')}`;
  return t('periods.startYear', { year });
}

export function hdLocksKeysPeriodText(
  gateInfo: HdGateInfoService,
  lang: string,
  periodId: HdLocksKeysPeriodId,
): HdLocksKeysPeriodText {
  const t = translator(gateInfo, lang);
  const meta = getHdLocksKeysPeriodMeta(periodId);
  const prefix = `periods.${periodId}`;
  const vesselCross = t(`${prefix}.vesselCross`) || meta.vesselCross;
  const sphinxCross = t(`${prefix}.sphinxCross`) || meta.sphinxCross;
  const generalKeynote = meta.keyShift === 0 ? t('periods.lockPhaseKeynote') : '';
  return {
    label: t(`${prefix}.label`) || hdLocksKeysYearRangeLabel(gateInfo, lang, meta),
    vesselCross,
    sphinxCross,
    crosses: t(`${prefix}.crosses`) || `${vesselCross} & ${sphinxCross}`,
    keynote: t(`${prefix}.keynote`) || generalKeynote,
    start: hdLocksKeysStartLabel(gateInfo, lang, meta),
  };
}

export function hdLocksKeysKeyText(
  gateInfo: HdGateInfoService,
  lang: string,
  lockGate: number,
  periodId: HdLocksKeysPeriodId,
): HdLocksKeysKeyText {
  const t = translator(gateInfo, lang);
  const prefix = `locks.${lockGate}.keys.${periodId}`;
  const meta = getHdLocksKeysPeriodMeta(periodId);
  const lock = getHdLocksKeysLockMeta(lockGate);
  const periodText = hdLocksKeysPeriodText(gateInfo, lang, periodId);
  const cross = lock?.group === 'sphinx' ? periodText.sphinxCross : periodText.vesselCross;
  const keyGate = hdLocksKeysKeyGate(lockGate, periodId);
  const circuitOf = (gate: number): string => {
    const circuitKey = gateInfo.gateInfoIn(gate, lang).mainCircuit?.key;
    return circuitKey == null ? '' : gateInfo.circuitNamesIn(lang)[circuitKey]?.name ?? '';
  };

  const customName = t(`${prefix}.name`);
  if (customName !== '') {
    // Eigen content mag circuit en kruis weglaten: die volgen dan uit de app-metadata.
    return {
      name: customName,
      circuit: t(`${prefix}.circuit`) || (meta.keyShift === 0 ? '' : circuitOf(keyGate)),
      cross: t(`${prefix}.cross`) || cross,
      description: t(`${prefix}.description`),
      planetaryNuance: t(`${prefix}.planetaryNuance`),
      channelNote: t(`${prefix}.channelNote`),
      starNote2027: t(`${prefix}.starNote2027`),
      isGeneric: false,
    };
  }

  if (meta.keyShift === 0) {
    return {
      name: t('detail.lockPhaseName'),
      circuit: '',
      cross,
      description: t('detail.lockPhaseText', { gate: lockGate }),
      planetaryNuance: '',
      channelNote: '',
      starNote2027: '',
      isGeneric: true,
    };
  }

  const info = gateInfo.gateInfoIn(keyGate, lang);
  return {
    name: info.translations.keynote2 || info.translations.keynote1,
    circuit: circuitOf(keyGate),
    cross,
    description: info.translations.shortIchingDescription,
    planetaryNuance: '',
    channelNote: '',
    starNote2027: '',
    isGeneric: true,
  };
}
